import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Trash2, Loader2, RefreshCw, Package } from 'lucide-react';
import { toast } from 'sonner';
import { inventoryAPI } from '@/lib/api';

export default function ScrapDisposalPage() {
  const [items, setItems] = useState([]);
  const [godown, setGodown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disposingRow, setDisposingRow] = useState(null);
  const [qty, setQty] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inventoryAPI.getScrapItems();
      setItems(res.data?.items || []);
      setGodown(res.data?.godown || null);
    } catch { toast.error('Failed to load scrap items'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDispose = (row) => {
    setDisposingRow(row);
    setQty(String(row.quantity));
    setRemarks('');
  };

  const submitDispose = async () => {
    const q = parseFloat(qty);
    if (!(q > 0)) { toast.error('Quantity must be > 0'); return; }
    if (q > disposingRow.quantity + 1e-6) { toast.error(`Max available: ${disposingRow.quantity}`); return; }
    setSubmitting(true);
    try {
      await inventoryAPI.disposeScrap({
        item_id: disposingRow.item_id,
        quantity: q,
        remarks,
      });
      toast.success(`Disposed ${q} ${disposingRow.uom} of ${disposingRow.item_name}`);
      setDisposingRow(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Disposal failed');
    } finally { setSubmitting(false); }
  };

  const totalItems = items.length;
  const totalQty = items.reduce((s, r) => s + (r.quantity || 0), 0);

  return (
    <div className="space-y-4 p-4" data-testid="scrap-disposal-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-rose-600" /> Scrap Disposal
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Items in <b>{godown?.name || 'Scrap Area'}</b> — dispose to scrap vendor. No revenue tracking; maintains audit trail only.
          </p>
        </div>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={load} disabled={loading} data-testid="scrap-refresh-btn">
          {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card data-testid="scrap-kpi-items">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Items in Scrap</p>
            <p className="text-lg font-semibold tabular-nums mt-0.5">{totalItems}</p>
          </CardContent>
        </Card>
        <Card data-testid="scrap-kpi-qty">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Total Units</p>
            <p className="text-lg font-semibold tabular-nums mt-0.5">{totalQty.toFixed(2)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="py-3 px-4 border-b">
          <CardTitle className="text-xs flex items-center gap-1.5"><Package className="w-3.5 h-3.5" /> Scrap inventory</CardTitle>
          <CardDescription className="text-[11px]">Click Dispose to remove a line. Action is audited.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center p-10"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : items.length === 0 ? (
            <p className="p-6 text-xs text-muted-foreground text-center">No items in scrap.</p>
          ) : (
            <table className="w-full text-xs" data-testid="scrap-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Item</th>
                  <th className="text-left px-3 py-2 font-medium">Unique ID</th>
                  <th className="text-right px-3 py-2 font-medium">Qty</th>
                  <th className="text-left px-3 py-2 font-medium">UOM</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(r => (
                  <tr key={r.item_id} className="border-t hover:bg-slate-50/60" data-testid={`scrap-row-${r.item_id}`}>
                    <td className="px-3 py-1.5 font-medium">{r.item_name}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-muted-foreground">{r.item_unique_id}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.quantity}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{r.uom}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Button size="sm" variant="outline" className="h-6 text-[10px]"
                        onClick={() => openDispose(r)} data-testid={`dispose-btn-${r.item_id}`}>
                        <Trash2 className="w-3 h-3 mr-1" /> Dispose
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!disposingRow} onOpenChange={(o) => { if (!o) setDisposingRow(null); }}>
        <DialogContent data-testid="dispose-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2"><Trash2 className="w-4 h-4 text-rose-600" /> Dispose from Scrap</DialogTitle>
            <DialogDescription className="text-[11px]">
              This reduces stock in the Scrap Area. Used for handover to a scrap vendor. No invoice or revenue is recorded.
            </DialogDescription>
          </DialogHeader>
          {disposingRow && (
            <div className="space-y-3 text-xs">
              <div className="border rounded p-2 bg-slate-50/60">
                <div className="font-medium">{disposingRow.item_name}</div>
                <div className="text-[10px] font-mono text-muted-foreground">{disposingRow.item_unique_id}</div>
                <div className="mt-1">Available: <Badge variant="outline" className="text-[10px]">{disposingRow.quantity} {disposingRow.uom}</Badge></div>
              </div>
              <div>
                <Label className="text-[11px]">Quantity to dispose</Label>
                <Input type="number" value={qty} onChange={e => setQty(e.target.value)} className="h-8 text-xs" data-testid="dispose-qty" />
              </div>
              <div>
                <Label className="text-[11px]">Remarks (optional)</Label>
                <Input placeholder="e.g. handed to ABC Scrap Vendor" value={remarks} onChange={e => setRemarks(e.target.value)} className="h-8 text-xs" data-testid="dispose-remarks" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDisposingRow(null)} disabled={submitting}>Cancel</Button>
            <Button size="sm" onClick={submitDispose} disabled={submitting} data-testid="dispose-confirm-btn">
              {submitting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />} Confirm Disposal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
